/**
 * Cockpit's binding of the shared `DataGridShell`.
 *
 * The grid shell itself lives in `@ampersandbrands/ui` and is shared with the
 * other Ampersand front ends, so it is deliberately ignorant of any one app's
 * DOM. The one thing it cannot infer is which element scrolls the page on
 * mobile, which it needs as its IntersectionObserver root to prefetch the next
 * page of rows 200px early. In Cockpit that element is the app shell's <main>
 * (`ui/src/shell/Layout.tsx`), marked with `data-cockpit-main`.
 *
 * This wrapper supplies that selector once so all 37 grid pages keep importing
 * a plain `DataGridShell` with no per-page prop to forget. Pages that need a
 * different scroller (a grid inside its own scroll container, say) can still
 * pass `scrollRootSelector` and it wins.
 *
 * Nothing else is wrapped. `gridTheadClassName` needs no Cockpit binding, so
 * pages import that string straight from `@ampersandbrands/ui` rather than
 * through here: re-exporting it would add indirection for no gain, and a
 * non-component re-export in a `.tsx` file breaks React Fast Refresh (the
 * eslint `react-refresh/only-export-components` rule).
 */

import { DataGridShell as SharedDataGridShell, type DataGridShellProps } from '@ampersandbrands/ui';

/** The app shell's scrolling <main>. See `ui/src/shell/Layout.tsx`. */
const COCKPIT_SCROLL_ROOT = '[data-cockpit-main]';

export function DataGridShell({
  scrollRootSelector = COCKPIT_SCROLL_ROOT,
  ...rest
}: DataGridShellProps) {
  return <SharedDataGridShell scrollRootSelector={scrollRootSelector} {...rest} />;
}
